// src/context/WebSocketContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";

const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const { token } = useParams();

  const WS_URL = process.env.REACT_APP_WS_URL;
  const wsRef = useRef(null);
  const handlers = useRef(new Map());
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const messageQueue = useRef([]); // Queue to hold messages before the connection opens

  // Connect to the WebSocket
  useEffect(() => {
    if (!token) {
      console.log("No token provided. Skipping WebSocket connection.");
      return;
    }

    let isMounted = true;

    const connect = () => {
      console.log("Connecting to WebSocket...");
      const ws = new WebSocket(`${WS_URL}/${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionStatus("connected");
        console.log("WebSocket connected");

        // Flush the message queue
        while (messageQueue.current.length > 0) {
          const message = messageQueue.current.shift();
          ws.send(JSON.stringify(message));
        }
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        const messageType = message.type;
        if (handlers.current.has(messageType)) {
          handlers.current.get(messageType).forEach((handler) => {
            handler(message);
          });
        } else {
          console.warn(
            `No handler registered for message type: ${messageType}`
          );
        }
      };

      ws.onclose = () => {
        setConnectionStatus("disconnected");
        console.log("WebSocket disconnected. Attempting to reconnect...");
        if (isMounted) {
          setTimeout(connect, 5000); // Retry after 5 seconds
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        ws.close();
      };
    };

    connect();

    return () => {
      isMounted = false;
      if (wsRef.current) wsRef.current.close();
    };
  }, [token, WS_URL]);

  // Register a handler for a specific message type
  const registerHandler = (messageType, handler) => {
    if (!handlers.current.has(messageType)) {
      handlers.current.set(messageType, []);
    }
    handlers.current.get(messageType).push(handler);
  };

  // Unregister a handler
  const unregisterHandler = (messageType, handler) => {
    if (handlers.current.has(messageType)) {
      const updatedHandlers = handlers.current
        .get(messageType)
        .filter((h) => h !== handler);
      if (updatedHandlers.length > 0) {
        handlers.current.set(messageType, updatedHandlers);
      } else {
        handlers.current.delete(messageType);
      }
    }
  };

  // Send a message through the WebSocket
  const sendMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn("WebSocket is not open. Queuing message...");
      messageQueue.current.push(message);
    }
  };

  return (
    <WebSocketContext.Provider
      value={{
        registerHandler,
        unregisterHandler,
        sendMessage,
        connectionStatus,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  return useContext(WebSocketContext);
};
